// NX input group (shadcn InputGroup restyled): input or textarea with leading or trailing addons such as a search icon or keycap.
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";

function InputGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="input-group"
      role="group"
      className={cn(
        "group/input-group relative flex w-full min-w-0 items-center rounded-sm border border-input bg-card transition-[border-color,box-shadow] duration-150",
        "h-9 has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>textarea]:h-auto",
        "has-[[data-slot=input-group-control]:focus-visible]:border-input-focus has-[[data-slot=input-group-control]:focus-visible]:shadow-[0_0_0_3px_var(--input-ring)]",
        "has-[[data-slot][aria-invalid=true]]:border-brand-text",
        className,
      )}
      {...props}
    />
  );
}

const inputGroupAddonVariants = cva(
  "flex items-center justify-center gap-1.5 text-ui text-muted-foreground select-none group-data-[disabled=true]/input-group:opacity-45 [&>svg:not([class*='size-'])]:size-[15px]",
  {
    variants: {
      align: {
        "inline-start": "order-first pl-2.5",
        "inline-end": "order-last pr-2",
        "block-end": "order-last w-full justify-start px-3 pb-2.5",
      },
    },
    defaultVariants: { align: "inline-start" },
  },
);

function InputGroupAddon({
  className,
  align = "inline-start",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    <div
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      {...props}
    />
  );
}

function InputGroupButton({
  className,
  type = "button",
  variant = "ghost",
  size = "icon-sm",
  ...props
}: React.ComponentProps<typeof Button>) {
  return (
    <Button type={type} variant={variant} size={size} className={cn("shadow-none", className)} {...props} />
  );
}

function InputGroupText({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span className={cn("flex items-center gap-1.5 text-ui text-muted-foreground", className)} {...props} />
  );
}

const controlBase =
  "min-w-0 flex-1 border-0 bg-transparent text-btn text-foreground outline-none placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-45";

function InputGroupInput({ className, ...props }: React.ComponentProps<"input">) {
  return (
    <input
      data-slot="input-group-control"
      className={cn(controlBase, "h-full px-2.5", className)}
      {...props}
    />
  );
}

function InputGroupTextarea({ className, ...props }: React.ComponentProps<"textarea">) {
  return (
    <textarea
      data-slot="input-group-control"
      className={cn(
        controlBase,
        "field-sizing-content min-h-11 w-full resize-none px-3 py-2.5 leading-normal",
        className,
      )}
      {...props}
    />
  );
}

export { InputGroup, InputGroupAddon, InputGroupButton, InputGroupText, InputGroupInput, InputGroupTextarea };
